import React, { useEffect, useMemo, useState } from 'react';
import Button from '../../shared/components/ui/Button';

export default function BulkAssignSalesManagerModal({
  units = [],
  salesManagerOptions = [],
  isSubmitting,
  onClose,
  onSubmit
}) {
  const [salesManagerId, setSalesManagerId] = useState('');
  const [selectedUnitIds, setSelectedUnitIds] = useState([]);

  const unitIds = useMemo(() => units.map((unit) => Number(unit.id)).filter(Boolean), [units]);

  useEffect(() => {
    setSelectedUnitIds([]);
    setSalesManagerId('');
  }, [unitIds.join(',')]);

  const allSelected = unitIds.length > 0 && unitIds.every((id) => selectedUnitIds.includes(id));

  const handleUnitToggle = (unitId, isChecked) => {
    const id = Number(unitId);
    if (isChecked) {
      setSelectedUnitIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    } else {
      setSelectedUnitIds((prev) => prev.filter((existingId) => existingId !== id));
    }
  };

  const handleSelectAll = (isChecked) => {
    setSelectedUnitIds(isChecked ? [...unitIds] : []);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(salesManagerId, selectedUnitIds);
  };

  return (
    <form onSubmit={handleSubmit} className="w-full">
      <p className="mb-4 text-sm text-gray-600">
        Select units and choose a sales manager to assign in bulk.
      </p>

      <div className="mb-6">
        <div className="mb-2 flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            Units <span className="text-red-500">*</span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-[#64748B]">
            <input
              type="checkbox"
              className="h-4 w-4 rounded border-gray-300 text-[#2B6777]"
              checked={allSelected}
              disabled={units.length === 0}
              onChange={(e) => handleSelectAll(e.target.checked)}
            />
            Select all ({units.length})
          </label>
        </div>

        <div className="max-h-[240px] overflow-y-auto rounded-lg border border-[#E7EBF1]">
          {units.length === 0 ? (
            <p className="px-4 py-6 text-center text-sm text-gray-500">No units in this level.</p>
          ) : (
            <ul className="divide-y divide-[#E7EBF1]">
              {units.map((unit) => {
                const id = Number(unit.id);
                return (
                  <li key={id}>
                    <label className="flex cursor-pointer items-start gap-3 px-4 py-3 hover:bg-[#F8FAFC]">
                      <input
                        type="checkbox"
                        className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-[#2B6777]"
                        checked={selectedUnitIds.includes(id)}
                        onChange={(e) => handleUnitToggle(id, e.target.checked)}
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium text-[#0F172A]">
                          {unit.unitName || `Unit ${id}`}
                        </span>
                        {unit.salesManagerName && unit.salesManagerName !== '—' && (
                          <span className="mt-0.5 block text-xs text-[#64748B]">
                            Current: {unit.salesManagerName}
                          </span>
                        )}
                      </span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {selectedUnitIds.length > 0 && (
          <p className="mt-2 text-sm text-[#334155]">
            {selectedUnitIds.length} unit{selectedUnitIds.length === 1 ? '' : 's'} selected
          </p>
        )}
      </div>

      <div className="mb-6">
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Sales Manager <span className="text-red-500">*</span>
        </label>
        <select
          value={salesManagerId}
          onChange={(e) => setSalesManagerId(e.target.value)}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#2B6777]">
          <option value="">Select sales manager</option>
          {salesManagerOptions.map((user) => (
            <option key={user.id} value={user.id}>
              {user.name || user.email || `User ${user.id}`}
            </option>
          ))}
        </select>
      </div>

      <div className="flex w-full items-center justify-end gap-3">
        <Button type="button" displayType="secondary" onClick={onClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={isSubmitting || !salesManagerId || selectedUnitIds.length === 0}>
          {isSubmitting ? 'Assigning...' : 'Assign'}
        </Button>
      </div>
    </form>
  );
}
